import React from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn,
} from 'material-ui/Table';
import Dialog from 'material-ui/Dialog';
import LinearProgress from 'material-ui/LinearProgress';
import SovosPaginationFooter from 's1-ui/sovos-pagination-footer';
import SovosFlatButton from 's1-ui/sovos-flat-button/SovosFlatButton';
import { gridHeaderColorGray } from 'theme/mainColors';
import { ALLOCATION_UNIT_PROP_TYPE } from 'shared/constants/PropTypes';
import ContextualGridMenuOptions from '../../contextualGridMenuOptions/ContextualGridMenuOptions';
import './allocationUnitsGrid.scss';

const headerLabelStyle = {
  color: gridHeaderColorGray,
  fontWeight: 400,
  fontSize: 15,
  paddingBottom: 0
};

class AllocationUnitsGrid extends React.Component {
  constructor(props){
    super(props);
    this.allocationUnitsGridColumns = [
      {
        title: 'Name',
        dataKey: 'name',
        width: 270
      },{
        title: 'Description',
        dataKey: 'description'
      },{
        title: 'Unit Count',
        dataKey: 'unitCount',
        width: 150
      },{
        title: '',
        width: 40,
        dataKey: 'moreOptions',
        disabled: !props.showMoreActionsIcon
      }
    ];
    this.state = {
      hoveredRow: -1,
      itemsPerPage: 10,
      page: 0,
      deleteAllocationUnitModalOpen: false,
      allocationUnitIdToDelete: -1
    };
  }

  handleRowHover = hoveredRow => this.setState({ hoveredRow });

  handleCellClick = rowID => this.props.onAllocationUnitSelect(this.props.allocationUnitsListGridData[rowID]);

  getStartItem = () => (this.props.totalItems ? ((this.state.itemsPerPage * (this.state.page)) + 1) : 0);

  calculateEndItem = () => {
    const maximumItemsOnPage = this.state.itemsPerPage * (this.state.page + 1);
    if (maximumItemsOnPage > this.props.totalItems) {
      return this.props.totalItems;
    }
    return maximumItemsOnPage;
  };

  handlePrevPage = ev => {
    ev.preventDefault();
    this.setState(oldState => {
      if(oldState.page > 0 ) {
        this.props.onPageChange(oldState.page - 1, oldState.itemsPerPage);
        return ({
          page: oldState.page - 1
        });
      }
      else {
        return ({});
      }
    });
  };

  handleNextPage = ev => {
    ev.preventDefault();
    this.setState(oldState => {
      if((oldState.page + 1) * oldState.itemsPerPage < this.props.totalItems) {
        this.props.onPageChange(oldState.page + 1, oldState.itemsPerPage);
        return ({
          page: oldState.page + 1
        });
      }
      else {
        return ({});
      }
    });
  };

  handlePerPageChanged = ev => {
    const pageSize = ev.target.value;
    this.setState({ page: 0, itemsPerPage: pageSize });
    this.props.onPageChange(0, pageSize);
  };

  handleEditAllocationUnit = allocationUnitId => this.props.onEditAllocationUnit(allocationUnitId);

  handleDeleteAllocationUnitClick = allocationUnitIdToDelete => this.setState({ deleteAllocationUnitModalOpen: true, allocationUnitIdToDelete });

  handleCloseDeleteAllocationUnit = () => this.setState({ deleteAllocationUnitModalOpen: false, allocationUnitIdToDelete: -1 });

  handleDeleteAllocationUnit = () => {
    this.props.onDeleteAllocationUnit(this.state.allocationUnitIdToDelete);
    this.handleCloseDeleteAllocationUnit();
  };

  render() {
    const { allocationUnitsListGridData, totalItems, fetchingAllocationUnits, showPagination, displayCheckbox, backgroundColor, errorMessage, openErrorDialog } = this.props;
    return (
      <div className="allocation-units-grid__main-container">
        {
          fetchingAllocationUnits &&
          <div className="allocation-units-grid__over-layer">
            <LinearProgress mode="indeterminate" />
          </div>
        }
        <div className={ `allocation-units-grid__table-container${ fetchingAllocationUnits ? ' blur-content' : '' }` }>
          {
            allocationUnitsListGridData.length > 0 &&
            <Table
              key="AllocationUnitsGrid"
              fixedHeader={ true }
              onRowHover={ this.handleRowHover }
              onRowHoverExit={ this.handleRowHoverExit }
              onCellClick={ this.handleCellClick }
              multiSelectable={ true }
              className="allocation-units-grid"
              style={ { backgroundColor: backgroundColor } }
            >
              <TableHeader displaySelectAll={ false } enableSelectAll={ false } adjustForCheckbox={ displayCheckbox } style={ { border: 'none' } }>
                <TableRow style={ { border: 'none' } }>
                  { this.allocationUnitsGridColumns.map(({ dataKey, width, title, disabled }) => (
                    !disabled && <TableHeaderColumn key={ dataKey } style={ { width: width || 'auto', ...headerLabelStyle } }>
                      { title }
                    </TableHeaderColumn>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody showRowHover={ true } displayRowCheckbox={ displayCheckbox } deselectOnClickaway={ false }>
                { allocationUnitsListGridData.map((allocationUnit, index) => (
                  <TableRow
                    id={ `import-allocation-unit-grid-row-${index}` }
                    key={ index }
                    hovered={ allocationUnit.disabled }
                    hoverable={ !allocationUnit.disabled }
                    selectable={ !allocationUnit.disabled }
                    selected={ allocationUnit.selected }
                    className={ `allocation-units-grid__table-row${ allocationUnit.disabled ? ' allocation-units-grid__table-row_not-selectable' : ''}` }
                  >
                    {
                      this.allocationUnitsGridColumns.map(({dataKey, width, disabled}) => (
                        !disabled && <TableRowColumn key={ dataKey } style={ {width: width || 'auto', textOverflow: `${dataKey !== 'moreOptions' ? 'ellipsis' : 'unset'}`} }>
                          {
                            (dataKey !== 'moreOptions') ?
                              allocationUnit[dataKey] :
                              this.state.hoveredRow === index && <ContextualGridMenuOptions elementId={ allocationUnit.id } editDisabled={ false } deleteDisabled={ false } onEdit={ this.handleEditAllocationUnit } onDelete={ this.handleDeleteAllocationUnitClick } />
                          }
                        </TableRowColumn>
                      ))
                    }
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          }
          {
            allocationUnitsListGridData.length > 0 && showPagination &&
            <SovosPaginationFooter
              key="AllocationUnitsPaginationFooter"
              endItem={ this.calculateEndItem() }
              itemsPerPage={ this.state.itemsPerPage }
              startItem={ this.getStartItem() }
              totalItems={ totalItems }
              onPrevClicked={ this.handlePrevPage }
              onNextClicked={ this.handleNextPage }
              onPerPageChanged={ this.handlePerPageChanged }
              itemsPerPageOptions={ [ 10, 25, 50 ] }
            />
          }
          <Dialog
            actions={ [
              <SovosFlatButton
                key="close-delete"
                color="secondary"
                onClick={ this.handleCloseDeleteAllocationUnit }
              >
                No
              </SovosFlatButton>,
              <SovosFlatButton
                key="submit-delete"
                onClick={ this.handleDeleteAllocationUnit }
              >
                Yes
              </SovosFlatButton>
            ] }
            modal={ true }
            open={ this.state.deleteAllocationUnitModalOpen }
          >
            Are you sure you want to delete this allocation unit?
          </Dialog>
          <Dialog
            actions={ [
              <SovosFlatButton
                key="submit-delete"
                onClick={ this.props.onCloseDialog }
              >
              Close
              </SovosFlatButton>
            ] }
            modal={ false }
            open={ openErrorDialog }
            onRequestClose={ this.props.onCloseDialog }
          >
            { errorMessage }
          </Dialog>
        </div>
      </div>
    );
  }
}

AllocationUnitsGrid.propTypes = {
  totalItems: PropTypes.number,
  allocationUnitsListGridData: PropTypes.arrayOf(ALLOCATION_UNIT_PROP_TYPE).isRequired,
  onAllocationUnitSelect: PropTypes.func,
  onDeleteAllocationUnit: PropTypes.func,
  onEditAllocationUnit: PropTypes.func,
  onPageChange: PropTypes.func,
  fetchingAllocationUnits: PropTypes.bool,
  showPagination: PropTypes.bool,
  displayCheckbox: PropTypes.bool,
  showRemoveIcon: PropTypes.bool,
  showMoreActionsIcon: PropTypes.bool,
  backgroundColor: PropTypes.string,
  errorMessage: PropTypes.string,
  openErrorDialog: PropTypes.bool,
  onCloseDialog: PropTypes.func
};

AllocationUnitsGrid.defaultProps = {
  fetchingAllocationUnits: false,
  onAllocationUnitSelect: () => null,
  onDeleteAllocationUnit: () => null,
  onEditAllocationUnit: () => null,
  totalItems: 0,
  onPageChange: () => null,
  showPagination: false,
  displayCheckbox: false,
  showRemoveIcon: false,
  showMoreActionsIcon: false,
  backgroundColor: '#fff',
  openErrorDialog: false
};

export default AllocationUnitsGrid;



// WEBPACK FOOTER //
// ./src/shared/components/allocationUnitsGrid/AllocationUnitsGrid.js